import React, { useEffect, useState } from 'react';
import { Alert, Button } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { GraduationApi, errorMessage } from '../services/GraduationApi';
import { useCanEvaluateUndergraduate, useCanViewUndergraduateApplications, useImpersonation } from './StudentWorkspace';

type ReportState = { key: string; url?: string; name?: string; student?: string; error?: string };
const unavailable = 'This DARS report is no longer available for this application.';
const denied = 'You do not have access to this DARS report.';

export default function DarsReport() {
  const route = useParams();
  const id = Number(route.id); const documentId = Number(route.documentId);
  const allowed = useCanViewUndergraduateApplications();
  const evaluator = useCanEvaluateUndergraduate();
  const impersonation = useImpersonation();
  const valid = Number.isSafeInteger(id) && id > 0 && Number.isSafeInteger(documentId) && documentId > 0;
  const key = `${id}:${documentId}:${evaluator}:${impersonation?.handle || ''}`;
  const [state, setState] = useState<ReportState | null>(null);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    if (!allowed || !valid) return;
    const controller = new AbortController();
    let objectUrl: string | undefined;
    setState(null);
    const load = async () => {
      try {
        const detail = await (evaluator ? GraduationApi.adminDetail : GraduationApi.viewerDetail)(id, controller.signal);
        if (controller.signal.aborted) return;
        const document = [...detail.documents].find(item => item.id === documentId);
        if (!document) throw new Error(unavailable);
        const file = await (evaluator ? GraduationApi.adminDocument : GraduationApi.viewerDocument)(id, documentId, controller.signal);
        if (controller.signal.aborted) return;
        objectUrl = URL.createObjectURL(file);
        setState({ key, url: objectUrl, name: document.name, student: detail.legalName });
      } catch (error) {
        if (!controller.signal.aborted && !axios.isCancel(error)) {
          const status = axios.isAxiosError(error) ? error.response?.status : undefined;
          setState({ key, error: status === 404 ? unavailable : status === 403 ? denied : errorMessage(error) });
        }
      }
    };
    load();
    return () => { controller.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [allowed, valid, id, documentId, evaluator, key, revision]);
  const report = state?.key === key ? state : null;
  return <article>
    {allowed && valid && <Link to={`/admin/undergraduate/${id}`} className="d-inline-block mb-3">← Back to application</Link>}
    <h1>DARS Report{report?.student && ` — ${report.student}`}</h1>
    {!allowed ? <Alert variant="danger" role="alert">{denied}</Alert> : !valid ?
      <Alert variant="danger" role="alert">A valid application and DARS report ID are required.</Alert> : <>
        {!report && <p role="status">Loading DARS report…</p>}
        {report?.error && <Alert variant="danger" role="alert">{report.error}{' '}
          <Button variant="outline-danger" size="sm" onClick={() => setRevision(value => value + 1)}>Try again</Button>
        </Alert>}
        {report?.url && <>
          <p><a href={report.url} download={report.name} className="btn btn-primary">Download DARS report</a></p>
          <p className="text-secondary">{report.name}. If the preview does not appear, download the report to view it.</p>
          <iframe title={`DARS report for ${report.student}`} src={report.url} style={{ width: '100%', height: '75vh', border: '1px solid #ced4da' }} />
        </>}
      </>}
  </article>;
}
